import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { permissionsApi } from '../../api/client';
import { ARTICLES } from './articles';
import { listUserGuides } from './userGuides';
import HelpDrawer from './HelpDrawer';

/**
 * Top-level help index. Browse-able list of every article available to the
 * current user, grouped roughly by audience.
 */
export default function HelpIndexPage() {
  const { data: branding } = useQuery({
    queryKey: ['branding', 'me'],
    queryFn: () => permissionsApi.branding(),
    staleTime: 60_000,
  });
  const role = (branding?.role || 'agent').toLowerCase();
  const isAdmin = role === 'admin';

  const [open, setOpen] = useState(null);

  // Show every article the user can possibly use. Tenant-bound supervisors
  // get the same set as Continuum supervisors — content doesn't change.
  const visible = Object.values(ARTICLES).filter((a) =>
    isAdmin || a.audience.includes(role)
  );

  const groups = {
    'For agents':     visible.filter((a) => a.audience.includes('agent')   && !a.audience.includes('supervisor')),
    'For supervisors':visible.filter((a) => a.audience.includes('supervisor') && !a.audience.includes('admin')),
    'For admins':     visible.filter((a) => a.audience.length === 1 && a.audience[0] === 'admin'),
    'For everyone':   visible.filter((a) => a.audience.length >= 2 && a.audience.includes('agent') && a.audience.includes('supervisor')),
  };

  // Long-form printable guides. Show every guide whose audience includes the
  // current role (admins see all).
  const guides = listUserGuides().filter(
    (g) => isAdmin || g.audience.includes(role)
  );

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-semibold mb-2">Help</h1>
      <p className="text-sm text-surface-muted mb-6">
        Click an article to open it. Each module also has a ❓ Help button in its top-right corner.
      </p>

      {guides.length > 0 ? (
        <section className="mb-8">
          <h2 className="text-sm uppercase tracking-wider text-surface-muted mb-2">
            📘 Full user guides
          </h2>
          <p className="text-xs text-surface-muted mb-2">
            Long-form walkthroughs designed to be copied into an email or a Word document.
            Each opens as its own printable page with a “Copy all” button.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {guides.map((g) => (
              <Link
                key={g.slug}
                to={`/help/guides/${g.slug}`}
                className="text-left p-3 rounded-thin border border-continuum-500/40 bg-continuum-500/5 hover:border-continuum-500 hover:bg-continuum-500/10 transition-colors"
              >
                <div className="text-sm font-medium">{g.title}</div>
                <div className="text-xs text-surface-muted mt-0.5">
                  {g.sections.length} sections · printable
                </div>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      {Object.entries(groups).map(([label, items]) => (
        items.length === 0 ? null : (
          <section key={label} className="mb-6">
            <h2 className="text-sm uppercase tracking-wider text-surface-muted mb-2">{label}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {items.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  onClick={() => setOpen(a.key)}
                  className="text-left p-3 rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-panel dark:bg-surface-dark-panel hover:border-continuum-500 hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle transition-colors"
                >
                  <div className="text-sm font-medium">{a.title}</div>
                  <div className="text-xs text-surface-muted mt-0.5">
                    {a.sections.length} section{a.sections.length === 1 ? '' : 's'}
                  </div>
                </button>
              ))}
            </div>
          </section>
        )
      ))}

      {open && <HelpDrawer moduleKey={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
